import { apiClient } from '@/lib/apiClient';
import { supabase } from '@/integrations/supabase/client';
import { API_BASE_URL } from '@/lib/apiBaseUrl';
import { CANVAS_ATTEMPT_SUPERSEDED, CANVAS_CANDIDATE_VERIFICATION_UNAVAILABLE, CANVAS_DRAFT_TRANSFER_TIMEOUT_MS, isDocxSizeLimitCode, type CanvasDraft, type CanvasDraftKind, type MovableDocumentCategory } from '@hector/shared-types';
import { canvasDraftRuntimeFingerprint } from './canvasDraftRuntime';

const BASE = '/canvas-drafts';

export { CANVAS_DRAFT_TRANSFER_TIMEOUT_MS } from '@hector/shared-types';

/** Issue codes of a `CANVAS_DOCX_NOT_SAVABLE` failure, or null for any other error. */
function canvasDocxIssueCodes(error: unknown): string[] | null {
  if (typeof error !== 'object' || error === null || !('errorCode' in error)
    || error.errorCode !== 'CANVAS_DOCX_NOT_SAVABLE' || !('details' in error)
    || typeof error.details !== 'object' || error.details === null || !('issues' in error.details)
    || !Array.isArray(error.details.issues)) return null;
  return error.details.issues.flatMap((issue: unknown) => (typeof issue === 'object' && issue !== null
    && 'code' in issue && typeof issue.code === 'string' ? [issue.code] : []));
}

/** A 422 can also mean a superseded attempt or a size ceiling: neither calls for correcting the format. */
export function isCanvasDocxFormatError(error: unknown): boolean {
  return canvasDocxIssueCodes(error)?.some((code) => code.startsWith('DOCX_') && !isDocxSizeLimitCode(code)) ?? false;
}

/** What the lawyer reads when the document exceeds a verification size ceiling, or null otherwise. */
export function canvasDocxTooLargeMessage(error: unknown): string | null {
  return canvasDocxIssueCodes(error)?.some(isDocxSizeLimitCode)
    ? 'Document trop volumineux pour être vérifié : scindez-le ou allégez-le.'
    : null;
}

/**
 * The server could not COMPLETE the structural check (#1743): no verdict on the
 * document, the last saved version is intact, and retrying is the whole fix —
 * so neither « correction de format » nor « contactez le support » applies.
 */
export function isCanvasDocxVerificationUnavailable(error: unknown): boolean {
  return canvasDocxIssueCodes(error)?.includes(CANVAS_CANDIDATE_VERIFICATION_UNAVAILABLE) ?? false;
}

/**
 * What the lawyer reads for it, wherever a save surfaces (indicator, dialog), or
 * null for any other error. The single source of this wording: the backend's
 * generic `userMessage` is never shown for this code.
 *
 * « Conservée » only when the server says a validated version exists
 * (`details.lastValidAvailable`): on a first save there is none to keep, and
 * promising one would be false.
 */
export function canvasDocxVerificationUnavailableMessage(error: unknown): string | null {
  if (!isCanvasDocxVerificationUnavailable(error)) return null;
  const details = (error as { details: Record<string, unknown> }).details;
  return details.lastValidAvailable === true
    ? 'Vérification indisponible — votre dernière version est conservée. Réessayez.'
    : 'Vérification indisponible — ce document n’a pas encore été enregistré. Réessayez.';
}

/**
 * True when a save, a download or a filing names an attempt that another page
 * of the same conversation has since replaced.
 *
 * A page never re-checks its acknowledged attempt: a page opened while the
 * agent was still writing in another one keeps the version of that moment, and
 * every exit is refused once the other page saves (prod 2026-10-26, #2479).
 * Retrying cannot help; loading the latest version does.
 */
export function isCanvasDocxSuperseded(error: unknown): boolean {
  return canvasDocxIssueCodes(error)?.includes(CANVAS_ATTEMPT_SUPERSEDED) ?? false;
}

export interface UpsertDraftInput {
  id: string;
  attemptId?: string;
  conversationId: string | null;
  caseId: string | null;
  title: string;
  kind: CanvasDraftKind;
  sourceDocumentId?: string | null;
  blob: Blob;
}

export interface SaveToCaseResult {
  documentId: string;
  filePath: string;
  aiProcessing: {
    status: 'submitted' | 'held';
    limit: number;
    totalSourceDocuments: number;
    jobIds?: string[];
  };
}

/**
 * Client des brouillons canvas. Le .docx voyage en multipart (apiClient.put
 * a la même branche FormData que post : Content-Type retiré pour laisser le
 * browser poser le boundary). L'id est généré côté client (uuid stable par
 * session de canvas) → PUT idempotent, ON CONFLICT gardé ownership côté DB.
 */
export const canvasDraftApi = {
  /**
   * @param signal Abandon du PUT. `ApiOptions extends RequestInit`, donc il
   * descend tel quel dans le `fetch` : c'est la seule plomberie qui manquait
   * pour qu'un enregistrement muet cesse de geler la résidence (#1334/D5).
   */
  async upsertDraft(input: UpsertDraftInput, signal?: AbortSignal): Promise<CanvasDraft> {
    const fd = new FormData();
    fd.append('file', input.blob, 'document.docx');
    if (input.attemptId) fd.append('attemptId', input.attemptId);
    if (input.conversationId) fd.append('conversationId', input.conversationId);
    if (input.caseId) fd.append('caseId', input.caseId);
    fd.append('title', input.title);
    fd.append('kind', input.kind);
    if (input.sourceDocumentId) fd.append('sourceDocumentId', input.sourceDocumentId);
    fd.append('runtimeFingerprint', JSON.stringify(canvasDraftRuntimeFingerprint));
    const res = await apiClient.put<{ draft: CanvasDraft }>(`${BASE}/${input.id}`, fd, { signal });
    return res.draft;
  },

  /**
   * @param signal Annulation du GET. `ApiOptions extends RequestInit`, donc il
   * descend tel quel dans le `fetch` — même plomberie que le `signal` d'`upsertDraft`.
   *
   * La VALEUR de la borne n'est délibérément pas définie ici : elle vit dans
   * `state/canvasDocumentOrchestration.ts` (`CANVAS_DRAFT_LOOKUP_TIMEOUT_MS`),
   * avec les deux seuls endroits qui la consomment — le signal posé par
   * `dependancesParDefaut` et l'échéance de `courseContreLEcheance`. Ce module-ci
   * est remplacé par un faux dans plusieurs suites : une constante d'échéance qui
   * y vivrait vaudrait `undefined` sous un faux partiel, et un `setTimeout(fn,
   * undefined)` se déclenche IMMÉDIATEMENT — la borne se retournerait en refus
   * instantané, vert en test et cassé en production (#1596).
   */
  async getDraftByConversation(conversationId: string, signal?: AbortSignal): Promise<CanvasDraft | null> {
    const res = await apiClient.get<{ draft: CanvasDraft | null }>(
      `${BASE}?conversationId=${encodeURIComponent(conversationId)}`,
      { signal },
    );
    return res.draft;
  },

  /**
   * TOUS les brouillons d'une conversation — back de « Documents de cette
   * conversation ».
   *
   * Le pendant de `getDraftByConversation`, qui rend le plus récent SEUL et
   * reste le chemin de restauration à l'ouverture du fil. Le serveur borne la
   * liste au-delà du plafond par conversation : une liste tronquée cacherait à
   * l'avocat un document qu'il a créé.
   */
  async listByConversation(conversationId: string): Promise<CanvasDraft[]> {
    const res = await apiClient.get<{ drafts: CanvasDraft[] }>(
      `${BASE}/conversation/${encodeURIComponent(conversationId)}`,
    );
    return res.drafts;
  },

  /**
   * Brouillons récents du user (métadonnées seules, pas le binaire) — back de
   * « Copier un brouillon d’une autre conversation ». Réponse maison `{ drafts }` (pas `{data}`).
   */
  async listRecent(limit = 20): Promise<CanvasDraft[]> {
    const res = await apiClient.get<{ drafts: CanvasDraft[] }>(
      `${BASE}/recent?limit=${encodeURIComponent(String(limit))}`,
    );
    return res.drafts;
  },

  /**
   * Download binaire same-origin JWT — apiClient n'a pas de helper blob :
   * mécanisme calqué sur `storageService.downloadFile` (token = session Supabase,
   * même `API_BASE_URL` partagé via `@/lib/apiBaseUrl`).
   */
  async getDraftContent(id: string): Promise<Blob> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token ?? '';
    const response = await fetch(`${API_BASE_URL}${BASE}/${id}/content`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(CANVAS_DRAFT_TRANSFER_TIMEOUT_MS),
    });
    if (!response.ok) {
      const contentType = response.headers.get('content-type') ?? '';
      const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null) as { message?: unknown } | null
        : null;
      const message = typeof payload?.message === 'string' ? payload.message : null;
      throw new Error(
        message ?? `canvas-drafts: échec download contenu (draftId=${id}, status=${response.status})`,
      );
    }
    return response.blob();
  },

  /**
   * Fences a local Word/PDF export against another browser publishing a newer
   * candidate. This deliberately checks metadata only: re-uploading a fresh
   * SuperDoc ZIP under the same attempt would violate byte immutability.
   */
  async verifyDraftAttempt(id: string, attemptId: string): Promise<void> {
    await apiClient.get(`${BASE}/${id}/attempts/${attemptId}`);
  },

  /**
   * Rend en PDF le `.docx` fourni — les octets de l'ÉDITEUR VIVANT, pas ceux du
   * brouillon stocké. C'est pour cela que la route ne prend pas d'id de brouillon :
   * convertir le dernier auto-save rendrait un acte en retard d'une frappe, et
   * l'avocat n'aurait aucun moyen de s'en apercevoir avant de l'avoir déposé.
   *
   * Conversion LibreOffice côté serveur : SuperDoc ne sait PAS produire de PDF.
   * Son type `ExportType` annonce `'docx' | 'pdf' | 'html'`, mais `export()` n'appelle
   * que `exportEditorsToDOCX` et ne pousse un blob que `if (exportType.includes("docx"))` —
   * demander `['pdf']` rend une archive VIDE, sans lever d'erreur (vérifié sur
   * superdoc@2.6.0, 2026-08-26). Ne pas « simplifier » en repassant côté client.
   *
   * Même mécanique same-origin JWT que `getDraftContent` (apiClient n'a pas de
   * helper blob). Timeout large : la conversion est bornée à 30 s côté serveur,
   * et l'attente d'un jeton de concurrence peut s'y ajouter.
   */
  async exportPdf(docx: Blob): Promise<Blob> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token ?? '';
    const fd = new FormData();
    fd.append('file', docx, 'document.docx');
    const response = await fetch(`${API_BASE_URL}${BASE}/export-pdf`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
      signal: AbortSignal.timeout(60_000),
    });
    if (!response.ok) {
      // Le message serveur (`userMessage`) distingue « expiré » de « saturé » : le
      // relayer tel quel, sinon l'avocat reçoit « échec » pour deux situations dont
      // une seule justifie de réessayer tout de suite.
      const detail = await response
        .json()
        .then((body: { message?: string; userMessage?: string }) => body.message || body.userMessage)
        .catch(() => null);
      throw new Error(
        detail || `canvas-drafts: échec conversion PDF (status=${response.status})`,
      );
    }
    return response.blob();
  },

  /**
   * Enregistre le brouillon comme pièce du dossier — porte de sortie du canvas.
   * Le backend copie l'objet S3 du brouillon sous une clé `cases/…` : le
   * brouillon reste donc ouvrable dans sa conversation après enregistrement.
   */
  async saveToCase(
    id: string,
    input: {
      caseId: string;
      documentCategory: MovableDocumentCategory;
      documentSubcategory?: string | null;
      documentName: string;
      /** Sous-dossier de destination ; absent ou vide = racine de l'onglet. */
      folderPath?: string[];
      mode?: 'create' | 'overwrite';
      targetDocumentId?: string | null;
      expectedAttemptId?: string;
    },
  ): Promise<SaveToCaseResult> {
    return apiClient.post<SaveToCaseResult>(
      `${BASE}/${id}/save-to-case`,
      input,
    );
  },

  async deleteDraft(id: string): Promise<boolean> {
    // apiClient.delete existe (apiClient.ts:125, `async delete<T = void>`).
    const res = await apiClient.delete<{ deleted: boolean }>(`${BASE}/${id}`);
    return res.deleted;
  },
};
